import { GetProjectsFromServerQuery } from "@/gql/graphql"
import { useSearchParams } from "next/navigation"
import { useEffect, useMemo, useRef, useState } from "react"
import { ApolloQueryResult, FetchMoreOptions, FetchMoreQueryOptions } from "@apollo/client"
import { captureException } from "@sentry/nextjs"

type PageInfo = {
  hasNextPage: boolean
  endCursor: string
}

export const usePortfolioProjects = ({
  shouldUseClientQueries,
  handle,
  clientCreative,
  serverCreative,
  fetchMoreClientSide,
  isFetchingFreshClientSideData,
}: {
  shouldUseClientQueries: boolean
  handle: string
  serverCreative: any
  clientCreative: any
  fetchMoreClientSide?: (
    fetchMoreOptions: FetchMoreQueryOptions<any, any> & FetchMoreOptions<any, any>
  ) => Promise<ApolloQueryResult<any>>
  isFetchingFreshClientSideData: boolean
}) => {
  const search = useSearchParams()
  const [loadingNextPage, setLoadingNextPage] = useState(false)
  const [serverSideProjects, setServerSideProjects] = useState(serverCreative?.creativeWorkPortfolioProjects?.edges)
  const [loading, setLoading] = useState(false)
  const [serverSidePageInfo, setServerSidePageInfo] = useState<PageInfo>(
    serverCreative?.creativeWorkPortfolioProjects?.pageInfo
  )
  const hasInitiallyLoaded = useRef(false)

  const fetchServerSideProjects = async (
    type: "fetchMore" | "fetchNew",
    roleFilters: string[],
    typeFilters: string[],
    signal: AbortSignal | null | undefined
  ) => {
    let res
    try {
      res = await fetch(
        `/api/portfolio/projects?handle=${handle}&first=12&after=${
          type === "fetchNew" ? null : serverSidePageInfo?.endCursor
        }${
          roleFilters && roleFilters.length > 0 ? `&creatorRoles=${roleFilters.join(",")}` : ""
        }${typeFilters && typeFilters.length > 0 ? `&categories=${typeFilters.join(",")}` : ""}`,
        { signal }
      )
    } catch (e) {
      captureException(e)
      return
    }
    const resJson = await res.json()
    const data: GetProjectsFromServerQuery = resJson.data
    const newProjects = data?.creativeByHandle?.creativeWorkPortfolioProjects
    const newEdges = newProjects?.edges

    if (newEdges && newEdges.length > 0) {
      setServerSideProjects((prevProjects: any) => {
        // In case of fetching new projects, replace the current list
        if (type === "fetchNew") {
          return newEdges
        }

        // In case of loading more, merge and de-duplicate projects
        const mergedProjects = [...prevProjects, ...newEdges]
        const deduplicatedProjects = mergedProjects.filter((project, index, self) => {
          const projectId = project?.node?.id
          return projectId && self.findIndex(p => p?.node?.id === projectId) === index
        })

        return deduplicatedProjects
      })
    }

    if (newProjects?.pageInfo) {
      setServerSidePageInfo(newProjects.pageInfo as PageInfo)
    }
  }

  const handleFetchMore = async () => {
    if (loadingNextPage) return
    if (
      shouldUseClientQueries &&
      clientCreative?.creativeWorkPortfolioProjects?.pageInfo?.hasNextPage &&
      !isFetchingFreshClientSideData
    ) {
      setLoadingNextPage(true)
      await fetchMoreClientSide?.({
        variables: {
          first: 12,
          after: clientCreative?.creativeWorkPortfolioProjects?.pageInfo?.endCursor,
        },
      })
    } else if (!shouldUseClientQueries) {
      if (!serverSidePageInfo?.hasNextPage) {
        return
      }
      setLoadingNextPage(true)
      const currentRoleParams = search?.get("role")?.split(",") || []
      const currentTypeParams = search?.get("type")?.split(",") || []
      await fetchServerSideProjects("fetchMore", currentRoleParams, currentTypeParams, null)
    }
    setLoadingNextPage(false)
  }

  const handleFetchNewProjects = async (
    roleFilters: string[],
    typeFilters: string[],
    signal: AbortSignal | null | undefined
  ) => {
    if (!loading) {
      setLoading(true)
      if (shouldUseClientQueries) {
      } else {
        await fetchServerSideProjects("fetchNew", roleFilters, typeFilters, signal)
      }
      setLoading(false)
    }
  }

  useEffect(() => {
    const controller = new AbortController()
    if (!shouldUseClientQueries) {
      const signal = controller.signal

      const currentRoleParams = search?.get("role")?.split(",") || []
      const currentTypeParams = search?.get("type")?.split(",") || []
      try {
        handleFetchNewProjects(currentRoleParams, currentTypeParams, signal)
      } catch (e) {
      } finally {
        if (!signal.aborted) {
          setLoading(false)
        }
      }
    }
    return () => {
      controller.abort("cleanup")
    }
  }, [search, shouldUseClientQueries])

  useEffect(() => {
    hasInitiallyLoaded.current = true
  }, [])

  const projects = useMemo(() => {
    if (shouldUseClientQueries) {
      if (isFetchingFreshClientSideData && hasInitiallyLoaded.current) {
        return []
      }
      return clientCreative?.creativeWorkPortfolioProjects?.edges || serverSideProjects
    }
    return serverSideProjects
  }, [shouldUseClientQueries, clientCreative, isFetchingFreshClientSideData, serverSideProjects])

  return {
    handleFetchMore,
    loading,
    setLoading,
    loadingNextPage,
    projects,
  }
}
